import { InfoItemHtml } from '@chiroup/components/InfoItemHtml';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { hcVerbiage } from '../../common/verbiage';
import { usePatientPortal } from '../../contexts/PatientPortalContext';
import FallbackImage from '../common/FallbackImage';
import ResponsivePlayer from './ResponsivePlayer';

const SECTION_LABELS: { key: string; label: string }[] = [
  { key: 'intro', label: 'Introduction' },
  { key: 'presentation', label: 'Presentation' },
  { key: 'management', label: 'Management' },
  { key: 'diagnosis', label: 'Diagnosis' },
  { key: 'references', label: 'References' },
  { key: 'pearls', label: 'Clinical Pearls' },
];

const ConditionDetail: React.FC<{ basePath: string }> = ({ basePath }) => {
  const { selectedPatient, language } = usePatientPortal();
  const { conditionId } = useParams();
  const primaryColor = selectedPatient?.clinic?.primaryColor || '#77bd43';

  const condition = useMemo(() => {
    return selectedPatient?.conditions?.find((c: any) => c.ID === Number(conditionId));
  }, [conditionId, selectedPatient?.conditions]);

  const { prevId, nextId } = useMemo(() => {
    const conditions = selectedPatient?.conditions || [];
    const idx = conditions.findIndex((c: any) => c.ID === Number(conditionId));

    return {
      prevId: conditions[idx - 1]?.ID || null,
      nextId: conditions[idx + 1]?.ID || null,
    };
  }, [conditionId, selectedPatient?.conditions]);

  if (!condition) {
    return (
      <div className="text-center py-16">
        <p className="text-sm text-gray-400 dark:text-gray-500">Condition not found.</p>
        <Link
          to={`${basePath}/conditions`}
          className="mt-4 inline-flex text-sm font-medium"
          style={{ color: primaryColor }}
        >
          Back to conditions
        </Link>
      </div>
    );
  }

  const hasVideo = !!condition.patientVideo;
  const hasImageOnly = !hasVideo && !!condition.commonImg;
  const hasNav = !!prevId || !!nextId;

  return (
    <div className="flex flex-col gap-5 max-w-3xl">
      <Link
        to={`${basePath}/conditions`}
        className="inline-flex items-center gap-1 text-sm text-gray-400 dark:text-gray-500 font-medium"
      >
        <ChevronLeftIcon className="h-4 w-4" />
        {hcVerbiage.conditions[language]}
      </Link>

      {hasVideo && (
        <div className="relative -mx-4 sm:-mx-6">
          <div className="overflow-hidden rounded-2xl mx-4 sm:mx-6">
            <ResponsivePlayer url={condition.patientVideo} />
          </div>
        </div>
      )}
      {hasImageOnly && (
        <div className="relative -mx-4 sm:-mx-6">
          <div className="mx-4 sm:mx-6">
            <FallbackImage
              src={condition.commonImg}
              alt={condition.commonName}
              imgClassName="w-full object-cover max-h-56 rounded-2xl"
            />
          </div>
        </div>
      )}

      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
          {condition.commonName}
        </h2>
        {condition.name && condition.name !== condition.commonName && (
          <p className="mt-1 text-sm text-gray-400 dark:text-gray-500">
            {hcVerbiage.clinicalName[language]}: {condition.name}
          </p>
        )}
      </div>

      {!!condition.descr && (
        <section className="portal-card p-4 flex flex-col gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
            Overview
          </h3>
          <div className="prose prose-sm prose-gray dark:prose-invert max-w-none leading-relaxed text-pretty">
            <InfoItemHtml useDivs className="normal-styling" value={condition.descr} />
          </div>
        </section>
      )}

      {/* Expandable reference sections. Preserved from the original
          layout but tightened to p-4 and switched to useDivs so the
          internal `<dd>` doesn't push the text off-axis. */}
      <div className="flex flex-col gap-2.5">
        {SECTION_LABELS.map(({ key, label }) => {
          const html = (condition as any)[key];

          if (!html) return null;

          return (
            <details key={key} className="portal-card overflow-hidden group">
              <summary className="flex items-center justify-between px-4 py-3.5 cursor-pointer list-none select-none font-semibold text-gray-900 dark:text-gray-100 text-sm">
                {label}
                <ChevronDownIcon
                  className="ml-2 h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500 group-open:rotate-180 transition-transform duration-200"
                  aria-hidden="true"
                />
              </summary>
              <div className="px-4 pb-4 pt-1 prose prose-sm prose-gray dark:prose-invert max-w-none border-t border-gray-100 dark:border-white/5 leading-relaxed text-pretty">
                <InfoItemHtml useDivs className="normal-styling" value={html} />
              </div>
            </details>
          );
        })}
      </div>

      {hasNav && (
        <div className="flex items-center justify-between pt-2">
          <div>
            {!!prevId && (
              <Link
                to={`${basePath}/conditions/${prevId}`}
                className="inline-flex items-center gap-1 text-sm font-medium text-gray-500 dark:text-gray-400"
              >
                <ChevronLeftIcon className="h-3.5 w-3.5" /> Previous
              </Link>
            )}
          </div>
          <div>
            {!!nextId && (
              <Link
                to={`${basePath}/conditions/${nextId}`}
                className="inline-flex items-center gap-1 text-sm font-semibold"
                style={{ color: primaryColor }}
              >
                Next <ChevronRightIcon className="h-3.5 w-3.5" />
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConditionDetail;
